import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Users, Database, TrendingUp, AlertTriangle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface UserSubscription {
  id: string;
  email: string;
  user_id: string;
  subscribed: boolean;
  subscription_tier: string;
  subscription_end: string | null;
  created_at: string;
  updated_at: string;
}

interface AdminStats {
  totalUsers: number;
  paidUsers: number;
  freeUsers: number;
  totalNoteSpaces: number;
  totalAnalysisSessions: number;
}

const Admin = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [sessionName] = useState('Admin Panel');
  const [users, setUsers] = useState<UserSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    paidUsers: 0,
    freeUsers: 0,
    totalNoteSpaces: 0,
    totalAnalysisSessions: 0
  });

  // Check if user is admin
  const isAdmin = user?.email === 'kieranbond97@gmail.com';

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/dashboard');
      return;
    }

    if (isAdmin) {
      loadAdminData();
    }
  }, [user, authLoading, isAdmin, navigate]);

  const loadAdminData = async () => {
    try {
      setLoading(true);

      console.log('Admin loading data, current user:', user?.email);

      // Direct query for all subscribers (should work with admin RLS policy)
      const { data: allSubscribers, error: subscribersError } = await supabase
        .from('subscribers')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Subscribers query result:', { allSubscribers, subscribersError });

      if (subscribersError) {
        console.error('Subscribers error:', subscribersError);
        throw subscribersError;
      }

      // Get all unique user IDs from both subscribers and note_spaces
      const { data: noteSpaceUsers, error: noteSpaceError } = await supabase
        .from('note_spaces')
        .select('user_id')
        .order('created_at', { ascending: false });

      if (noteSpaceError) throw noteSpaceError;

      // Combine user IDs from both sources
      const subscriberUserIds = allSubscribers?.map(s => s.user_id).filter(Boolean) || [];
      const noteSpaceUserIds = noteSpaceUsers?.map(ns => ns.user_id) || [];
      const allUserIds = [...new Set([...subscriberUserIds, ...noteSpaceUserIds])];

      console.log('All user IDs:', allUserIds);

      // Get email addresses for all users
      const userEmails = await Promise.all(
        allUserIds.map(async (userId) => {
          const { data } = await supabase.rpc('get_user_email_by_id', { user_uuid: userId });
          return { user_id: userId, email: data };
        })
      );

      console.log('User emails:', userEmails);

      // Create complete user list by merging user data with subscriber data
      const completeUsers = userEmails.map(({ user_id, email }) => {
        const subscriber = allSubscribers?.find(s => s.user_id === user_id);
        return {
          id: subscriber?.id || `temp-${user_id}`,
          email: email || 'Unknown',
          user_id,
          subscribed: subscriber?.subscribed || false,
          subscription_tier: subscriber?.subscription_tier || 'free',
          subscription_end: subscriber?.subscription_end || null,
          created_at: subscriber?.created_at || new Date().toISOString(),
          updated_at: subscriber?.updated_at || new Date().toISOString()
        };
      });

      console.log('Complete users:', completeUsers);
      setUsers(completeUsers);

      // Load stats
      const { data: noteSpaces, error: noteSpacesError } = await supabase
        .from('note_spaces')
        .select('id');
      
      const { data: analysisSessions, error: analysisError } = await supabase
        .from('analysis_sessions')
        .select('id');

      if (noteSpacesError) throw noteSpacesError;
      if (analysisError) throw analysisError;

      const totalUsers = completeUsers.length;
      const paidUsers = completeUsers.filter(u => u.subscribed && (u.subscription_tier === 'pro' || u.subscription_tier === 'expert')).length;
      const freeUsers = totalUsers - paidUsers;

      setStats({
        totalUsers,
        paidUsers,
        freeUsers,
        totalNoteSpaces: noteSpaces?.length || 0,
        totalAnalysisSessions: analysisSessions?.length || 0
      });

    } catch (error) {
      console.error('Error loading admin data:', error);
      toast({
        title: "Error loading admin data",
        description: "Failed to load admin dashboard. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserSubscription = async (userId: string, tier: string) => {
    try {
      const isSubscribed = tier !== 'free';
      const subscriptionEnd = isSubscribed ? 
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : // 30 days from now
        null;

      // Get user email for potential insert
      const user = users.find(u => u.user_id === userId);
      const userEmail = user?.email || '';

      // Try to update first, if no rows affected, then insert
      const { data: updateData, error: updateError } = await supabase
        .from('subscribers')
        .update({
          subscribed: isSubscribed,
          subscription_tier: tier,
          subscription_end: subscriptionEnd,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select();

      // If no rows were updated, insert a new record
      if (!updateError && (!updateData || updateData.length === 0)) {
        const { error: insertError } = await supabase
          .from('subscribers')
          .insert({
            user_id: userId,
            email: userEmail,
            subscribed: isSubscribed,
            subscription_tier: tier,
            subscription_end: subscriptionEnd
          });
        
        if (insertError) throw insertError;
      } else if (updateError) {
        throw updateError;
      }

      // Update local state
      setUsers(prev => prev.map(user => 
        user.user_id === userId 
          ? { 
              ...user, 
              subscribed: isSubscribed, 
              subscription_tier: tier,
              subscription_end: subscriptionEnd,
              updated_at: new Date().toISOString()
            }
          : user
      ));

      // Update stats
      setStats(prev => {
        const wasSubscribed = users.find(u => u.user_id === userId)?.subscribed && 
                             (users.find(u => u.user_id === userId)?.subscription_tier === 'pro' || 
                              users.find(u => u.user_id === userId)?.subscription_tier === 'expert');
        const isNowSubscribed = isSubscribed && (tier === 'pro' || tier === 'expert');
        
        let paidChange = 0;
        if (!wasSubscribed && isNowSubscribed) paidChange = 1;
        if (wasSubscribed && !isNowSubscribed) paidChange = -1;

        return {
          ...prev,
          paidUsers: prev.paidUsers + paidChange,
          freeUsers: prev.freeUsers - paidChange
        };
      });

      toast({
        title: "Subscription updated",
        description: `User subscription changed to ${tier}`,
      });

    } catch (error) {
      console.error('Error updating subscription:', error);
      toast({
        title: "Error updating subscription",
        description: "Failed to update user subscription. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not admin
  if (!isAdmin) {
    return null; // Will be redirected by useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        sessionName={sessionName}
        onSessionNameChange={() => {}}
      />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-red-500" />
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <p className="text-gray-600">Manage users, subscriptions, and system analytics.</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-sm text-gray-600">Paid Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.paidUsers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Free Users</p>
                <p className="text-2xl font-bold text-gray-900">{stats.freeUsers}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-sm text-gray-600">Note Spaces</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalNoteSpaces}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-sm text-gray-600">Analyses</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalAnalysisSessions}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* User Management */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-amber-500" />
              <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
            </div>
            <Button
              onClick={loadAdminData}
              variant="outline"
              disabled={loading}
            >
              Refresh Data
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading user data...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.subscribed ? "default" : "secondary"}>
                          {user.subscribed ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                       <TableCell>
                         <Badge 
                           variant={user.subscription_tier === 'pro' || user.subscription_tier === 'expert' ? "default" : "outline"}
                           className={
                             user.subscription_tier === 'pro' ? "bg-green-100 text-green-800" :
                             user.subscription_tier === 'expert' ? "bg-purple-100 text-purple-800" : ""
                           }
                         >
                           {user.subscription_tier}
                         </Badge>
                       </TableCell>
                      <TableCell>
                        {user.subscription_end ? 
                          new Date(user.subscription_end).toLocaleDateString() : 
                          'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={user.subscription_tier}
                          onValueChange={(value) => updateUserSubscription(user.user_id, value)}
                        >
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="expert">Expert</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {users.length === 0 && (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No users found.</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Admin;